export type ReportingTone =
	| "primary"
	| "secondary"
	| "muted"
	| "danger"
	| "warning"
	| `categorical-${number}`

export interface ReportingSeries {
	key: string
	label: string
	tone: ReportingTone
}

const CATEGORICAL = [
	"#6EC499",
	"#4ADE80",
	"#FB923C",
	"#A78BFA",
	"#60A5FA",
	"#38BDF8",
	"#F87171",
	"#FBBF24",
	"#2DD4BF",
	"#C084FC",
] as const

// Room under the bars for the date labels; a card beside a chart lines up with its baseline.
export const REPORTING_X_AXIS_HEIGHT = 30

export const reportingChartTheme = {
	primary: "#6EC499",
	primaryBright: "#4CB782",
	secondary: "#A78BFA",
	muted: "#9CA3AF",
	danger: "#F87171",
	warning: "#FBBF24",
	grid: {
		light: "#e5e7eb",
		dark: "#374151",
	},
	axis: {
		light: "#6b7280",
		dark: "#9ca3af",
	},
	areaFillOpacity: 0.22,
	barRadius: 0,
	maxBarSize: 12,
} as const

// The inner gridlines sit well under the baseline so the bars read first; the
// baseline keeps the full border weight because it is the chart's floor.
export const reportingGridClassName =
	"stroke-gray-200/40 dark:stroke-gray-700/40"

export const reportingBaselineClassName = "stroke-gray-200 dark:stroke-gray-700"

export const reportingCardClassName =
	"rounded-xl border border-gray-200 bg-transparent dark:border-gray-700/80"

export const reportingCardLoadingClassName =
	"animate-pulse rounded-xl border border-gray-200 bg-transparent dark:border-gray-700/80"

export const toneColor = (tone: ReportingTone): string => {
	if (tone.startsWith("categorical-")) {
		const index = Number(tone.slice("categorical-".length))
		if (Number.isFinite(index)) {
			return CATEGORICAL[Math.abs(index) % CATEGORICAL.length] ?? CATEGORICAL[0]
		}
	}
	switch (tone) {
		case "primary":
			return reportingChartTheme.primary
		case "secondary":
			return reportingChartTheme.secondary
		case "muted":
			return reportingChartTheme.muted
		case "danger":
			return reportingChartTheme.danger
		case "warning":
			return reportingChartTheme.warning
		default:
			return reportingChartTheme.primary
	}
}

export const categoricalColor = (index: number): string =>
	CATEGORICAL[Math.abs(index) % CATEGORICAL.length] ?? CATEGORICAL[0]

export type HeatmapScale = "activity" | "danger"

const HEATMAP_BASE: Record<HeatmapScale, string> = {
	activity: reportingChartTheme.primary,
	danger: reportingChartTheme.danger,
}

const hexToRgb = (hex: string): [number, number, number] => {
	const value = hex.replace("#", "")
	return [
		Number.parseInt(value.slice(0, 2), 16),
		Number.parseInt(value.slice(2, 4), 16),
		Number.parseInt(value.slice(4, 6), 16),
	]
}

export const heatmapColorAt = (
	intensity: number,
	scale: HeatmapScale = "activity",
): string => {
	const t = Math.min(1, Math.max(0, intensity))
	const [r, g, b] = hexToRgb(HEATMAP_BASE[scale])
	const alpha = Math.round((0.16 + t * 0.84) * 1000) / 1000
	return `rgba(${r}, ${g}, ${b}, ${alpha})`
}
